import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { fetchAllShows, saveShow } from "../api/client";
import RecoveryPanel from "../components/RecoveryPanel";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import EmptyState from "../components/ui/EmptyState";
import LoadingState from "../components/ui/LoadingState";
import PosterFrame from "../components/ui/PosterFrame";
import ShowCard from "../components/ShowCard";
import {
  getTrailerUrl,
  getWatchUrl,
  listText,
  normalizeTags,
  showMatchesKey,
  showPath,
} from "../utils/showDisplay";
import { getCurrentUser } from "../utils/auth";

function platformOptions(platform = "") {
  return String(platform || "")
    .split(/[/,]/)
    .map((value) => value.trim())
    .filter(Boolean);
}

function activeWarnings(contentFlags = {}) {
  return Object.entries(contentFlags || {})
    .filter(([_, flagged]) => flagged)
    .map(([flag]) => flag.replace(/_/g, " "));
}

export default function ShowDetails() {
  const { showKey } = useParams();
  const location = useLocation();
  const routedShow = location.state?.show || null;
  const [catalog, setCatalog] = useState([]);
  const [status, setStatus] = useState(routedShow ? "success" : "loading");
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [isSavingShow, setIsSavingShow] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  useEffect(() => {
    let active = true;

    fetchAllShows({ limit: 100, sort: "title" })
      .then((response) => {
        if (!active) return;

        const results = response?.data?.results;
        if (!Array.isArray(results)) {
          setCatalog([]);
          setStatus(routedShow ? "success" : "error");
          setError("The catalog response was not in the expected shape.");
          return;
        }

        setCatalog(results);
        setStatus("success");
      })
      .catch((err) => {
        if (!active) return;
        setCatalog([]);
        setStatus(routedShow ? "success" : "error");
        setError(err?.response?.data?.detail || "The show details could not load.");
      });

    return () => {
      active = false;
    };
  }, [routedShow]);

  useEffect(() => {
    let active = true;

    getCurrentUser()
      .then((user) => {
        if (active) setCurrentUser(user);
      })
      .catch(() => {
        if (active) setCurrentUser(null);
      });

    return () => {
      active = false;
    };
  }, []);

  const show = useMemo(() => {
    if (routedShow && showMatchesKey(routedShow, showKey)) return routedShow;
    return catalog.find((item) => showMatchesKey(item, showKey)) || routedShow;
  }, [catalog, routedShow, showKey]);

  const similarShows = useMemo(() => {
    if (!show) return [];

    const showGenres = new Set((show.genres || []).map((genre) => genre.toLowerCase()));
    return catalog
      .filter((item) => item.title !== show.title)
      .filter((item) =>
        (item.genres || []).some((genre) => showGenres.has(genre.toLowerCase()))
      )
      .slice(0, 3);
  }, [catalog, show]);

  if (status === "loading") {
    return (
      <div className="px-4 py-10">
        <LoadingState title="Loading show details" message="Pulling the pitch card into focus." />
      </div>
    );
  }

  if (!show && status === "error") {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <RecoveryPanel
          title="Show details did not load"
          message={error}
          actions={[
            { label: "Discover Shows", to: "/discover" },
            { label: "Try Again", onClick: () => window.location.reload() },
          ]}
        />
      </div>
    );
  }

  if (!show) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-10">
        <EmptyState
          title="Show not found"
          message="That title is not in the current catalog view."
          primaryAction={{ label: "Discover Shows", to: "/discover" }}
        />
      </div>
    );
  }

  const pitch = show.pitch || {};
  const warnings = activeWarnings(show.content_flags);
  const platforms = platformOptions(show.platform);
  const tags = normalizeTags([
    ...(show.genres || []),
    ...(show.moods || []),
    ...(show.cultural_tags || []),
  ]).slice(0, 8);
  const watchUrl = getWatchUrl(show);
  const trailerUrl = getTrailerUrl(show);

  const handleSaveShow = async () => {
    if (!show.id || !currentUser) return;

    try {
      setIsSavingShow(true);
      setSaveStatus("");
      await saveShow(show.id);
      setSaveStatus("Saved to your collection.");
    } catch (err) {
      setSaveStatus("Could not save this show.");
    } finally {
      setIsSavingShow(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <Link to="/discover" className="mb-6 inline-flex min-h-11 items-center text-sm font-bold text-hookd-empress">
        ← Back to Discover
      </Link>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(260px,300px)]">
        <section className="min-w-0">
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(240px,340px)] xl:items-start">
            <div>
              <h1 className="font-display text-5xl font-bold leading-tight text-hookd-cream">
                {show.title}
              </h1>
              <p className="mt-4 text-hookd-muted">
                {show.age_rating || "Rating not set"} • {show.commitment_level || "Format not set"} •{" "}
                {show.platform || "Platform unknown"}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Badge variant="purple">#1 Match For You</Badge>
                <Badge variant="gold">High Vibe Match</Badge>
              </div>

              <section className="mt-8">
                <h2 className="mb-2 text-sm font-bold uppercase text-hookd-gold">The Hook</h2>
                <p className="text-base leading-7 text-hookd-cream">
                  {pitch.hook ||
                    pitch.empress_hook ||
                    "A curated title from the Hook'd catalog."}
                </p>
              </section>
            </div>

            <PosterFrame
              show={show}
              className="aspect-video min-h-0 w-full max-w-full self-start"
            />
          </div>

          <Card className="mt-6 p-5">
            <div className="grid gap-5 md:grid-cols-3">
              <section>
                <h2 className="mb-2 text-sm font-bold uppercase text-hookd-gold">
                  Why You'll Love It
                </h2>
                <p className="text-sm leading-6 text-hookd-muted">
                  {pitch.why_youll_love_it || "This pick is grounded in the tags and vibe signals currently available for the title."}
                </p>
              </section>
              <section>
                <h2 className="mb-2 text-sm font-bold uppercase text-hookd-gold">
                  Binge Strategy
                </h2>
                <p className="text-sm leading-6 text-hookd-muted">
                  {pitch.binge_strategy || pitch.commitment || show.commitment_level || "Watch when the vibe is right."}
                </p>
              </section>
              <section>
                <h2 className="mb-2 text-sm font-bold uppercase text-hookd-gold">Vibe</h2>
                <div className="flex flex-wrap gap-2">
                  {tags.length ? tags.map((tag) => <Badge key={tag}>{tag}</Badge>) : <Badge variant="neutral">Curated</Badge>}
                </div>
              </section>
            </div>
          </Card>

          <Card className="mt-6 p-5">
            <h2 className="mb-3 text-sm font-bold uppercase text-hookd-gold">Safety Note</h2>
            <p className="text-sm leading-6 text-hookd-muted">
              {pitch.safety_note || (warnings.length ? warnings.join(", ") : "No content warnings are currently flagged for this title.")}
            </p>
          </Card>

          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <Card className="p-5">
              <h2 className="font-display text-2xl font-bold text-hookd-cream">
                More Details
              </h2>
              <dl className="mt-5 grid gap-4 text-sm">
                <div>
                  <dt className="font-bold text-hookd-gold">Genre</dt>
                  <dd className="text-hookd-muted">{listText(show.genres)}</dd>
                </div>
                <div>
                  <dt className="font-bold text-hookd-gold">Mood</dt>
                  <dd className="text-hookd-muted">{listText(show.moods)}</dd>
                </div>
                <div>
                  <dt className="font-bold text-hookd-gold">Cultural Vibe</dt>
                  <dd className="text-hookd-muted">{listText(show.cultural_tags)}</dd>
                </div>
                <div>
                  <dt className="font-bold text-hookd-gold">Laugh Preference</dt>
                  <dd className="text-hookd-muted">{show.laugh_factor || "Not set"}</dd>
                </div>
              </dl>
            </Card>

            <Card className="p-5">
              <div className="mb-5 flex items-center justify-between gap-3">
                <h2 className="font-display text-2xl font-bold text-hookd-cream">
                  Similar Shows
                </h2>
                <Link to="/discover" className="text-sm font-bold text-hookd-empress">
                  View all →
                </Link>
              </div>
              {similarShows.length ? (
                <div className="grid gap-3">
                  {similarShows.map((item) => (
                    <ShowCard
                      key={item.id || item.title}
                      show={item}
                      detailsTo={showPath(item)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm leading-6 text-hookd-muted">
                  Similar titles will appear when the catalog has genre overlap.
                </p>
              )}
            </Card>
          </div>
        </section>

        <aside className="grid content-start gap-5">
          <Card className="p-5">
            <h2 className="font-display text-2xl font-bold text-hookd-cream">
              Where to Watch
            </h2>
            <div className="mt-5 grid gap-3">
              <Button
                as="a"
                href={watchUrl}
                target="_blank"
                rel="noreferrer"
                variant="accent"
                className="w-full"
              >
                Watch Now
              </Button>
              <Button
                as="a"
                href={trailerUrl}
                target="_blank"
                rel="noreferrer"
                variant="ghost"
                className="w-full"
              >
                Trailer
              </Button>
            </div>
            <div className="mt-5 grid gap-3">
              {(platforms.length ? platforms : ["Platform unavailable"]).map((platform) => (
                <a
                  key={platform}
                  href={watchUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between rounded-hookd border border-hookd-line bg-white/5 p-3"
                >
                  <span className="font-semibold text-hookd-cream">{platform}</span>
                  <span className="text-hookd-muted">›</span>
                </a>
              ))}
            </div>
            {currentUser && show.id ? (
              <Button
                className="mt-5 w-full"
                onClick={handleSaveShow}
                loading={isSavingShow}
              >
                {saveStatus.startsWith("Saved") ? "Saved" : "Save Show"}
              </Button>
            ) : (
              <Button as={Link} to="/login" className="mt-5 w-full">
                Sign In To Save
              </Button>
            )}
            {saveStatus && (
              <p className="mt-3 text-sm text-hookd-muted">{saveStatus}</p>
            )}
          </Card>

          <Card className="p-5">
            <h2 className="font-display text-2xl font-bold text-hookd-cream">
              Recommendation DNA
            </h2>
            <dl className="mt-5 grid gap-4 text-sm">
              <div>
                <dt className="font-bold text-hookd-gold">Commitment</dt>
                <dd className="text-hookd-muted">{show.commitment_level || "Not set"}</dd>
              </div>
              <div>
                <dt className="font-bold text-hookd-gold">Identity Tags</dt>
                <dd className="text-hookd-muted">{listText(show.identity_tags)}</dd>
              </div>
              <div>
                <dt className="font-bold text-hookd-gold">Representation</dt>
                <dd className="text-hookd-muted">{listText(show.representation_tags)}</dd>
              </div>
              <div>
                <dt className="font-bold text-hookd-gold">Empress Tags</dt>
                <dd className="text-hookd-muted">{listText(show.empress_tags)}</dd>
              </div>
            </dl>
          </Card>
        </aside>
      </div>

      {status === "success" && error && (
        <div className="mt-6">
          <RecoveryPanel
            title="Live catalog refresh was limited"
            message={error}
            variant="warning"
          />
        </div>
      )}
    </div>
  );
}
